import { useCallback, useEffect, useState } from "react";
import { Archive, ArchiveRestore, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, apiError, formatDate, money } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const blank = {
  parent_name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  country: "United States",
  partner_id: "",
  enrollment_type: "initial",
  payment_status: "unpaid",
};

export default function AdminFamilies() {
  const [families, setFamilies] = useState(null);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [payment, setPayment] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/families", { params: { search, archived } });
    setFamilies(data);
  }, [search, archived]);

  useEffect(() => {
    api.get("/admin/partners").then(({ data }) => setPartners(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => toast.error(apiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, partner_id: form.partner_id || null };
      if (editingId) await api.put(`/admin/families/${editingId}`, payload);
      else await api.post("/admin/families", payload);
      toast.success(editingId ? "Family updated" : "Family added");
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setArchive = async (family, value) => {
    await api.post(`/admin/families/${family.id}/archive?archived=${value}`);
    toast.success(value ? "Family archived" : "Family restored");
    load();
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/families/${confirmDelete.id}`);
      toast.success("Family deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePayment = async () => {
    try {
      await api.put(`/admin/families/${payment.id}/payment`, {
        payment_status: payment.payment_status,
        payment_amount: Number(payment.payment_amount || 0),
      });
      toast.success("Payment updated");
      setPayment(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Families"
      subtitle="Enrolled families and payment status"
      actions={
        <Button
          className="gap-2"
          onClick={() => {
            setForm({ ...blank });
            setEditingId(null);
          }}
          data-testid="add-family-button"
        >
          <Plus className="h-4 w-4" /> Add family
        </Button>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search families by name, email, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="family-search-input"
          />
        </div>
        <Button
          variant={archived ? "default" : "outline"}
          size="sm"
          onClick={() => setArchived(!archived)}
          data-testid="toggle-archived-families"
        >
          {archived ? "Viewing archived" : "View archived"}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="overflow-x-auto p-0">
          {!families ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table data-testid="families-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Parent / guardian</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Enrolled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {families.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-sm text-slate-500">
                      No families found.
                    </TableCell>
                  </TableRow>
                )}
                {families.map((f) => (
                  <TableRow key={f.id} data-testid={`family-row-${f.id}`}>
                    <TableCell className="font-medium text-slate-900">{f.parent_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {f.email}
                      <div className="text-xs text-slate-400">{f.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {[f.city, f.state].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{f.partner_name || "—"}</TableCell>
                    <TableCell>{f.student_count}</TableCell>
                    <TableCell>
                      <button
                        onClick={() =>
                          setPayment({
                            id: f.id,
                            payment_status: f.payment_status,
                            payment_amount: f.payment_amount || 0,
                          })
                        }
                        data-testid={`payment-badge-${f.id}`}
                      >
                        <StatusBadge status={f.payment_status} />
                      </button>
                      <div className="mt-1 text-xs text-slate-400">{money(f.payment_amount)}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatDate(f.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setForm({
                              parent_name: f.parent_name,
                              email: f.email,
                              phone: f.phone || "",
                              address: f.address || "",
                              city: f.city || "",
                              state: f.state || "",
                              zip_code: f.zip_code || "",
                              country: f.country || "United States",
                              partner_id: f.partner_id || "",
                              enrollment_type: f.enrollment_type || "initial",
                              payment_status: f.payment_status || "unpaid",
                            });
                            setEditingId(f.id);
                          }}
                          data-testid={`edit-family-${f.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchive(f, !f.archived)}
                          data-testid={`archive-family-${f.id}`}
                        >
                          {f.archived ? (
                            <ArchiveRestore className="h-4 w-4" />
                          ) : (
                            <Archive className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => setConfirmDelete(f)}
                          data-testid={`delete-family-${f.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit family" : "Add family"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ["parent_name", "Parent / guardian legal name", "sm:col-span-2"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["address", "Mailing address", "sm:col-span-2"],
                ["city", "City"],
                ["state", "State"],
                ["zip_code", "ZIP / postal code"],
                ["country", "Country"],
              ].map(([key, label, cls = ""]) => (
                <div key={key} className={`space-y-2 ${cls}`}>
                  <Label>{label}</Label>
                  <Input
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`family-${key}-input`}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label>Educational partner</Label>
                <Select
                  value={form.partner_id}
                  onValueChange={(v) => setForm({ ...form, partner_id: v })}
                >
                  <SelectTrigger data-testid="family-partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Enrollment type</Label>
                <Select
                  value={form.enrollment_type}
                  onValueChange={(v) => setForm({ ...form, enrollment_type: v })}
                >
                  <SelectTrigger data-testid="family-enrollment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">Initial enrollment</SelectItem>
                    <SelectItem value="reenrollment">Annual re-enrollment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment status</Label>
                <Select
                  value={form.payment_status}
                  onValueChange={(v) => setForm({ ...form, payment_status: v })}
                >
                  <SelectTrigger data-testid="family-payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["unpaid", "pending", "paid", "waived", "refunded"].map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={save} data-testid="save-family-button">
              Save family
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(payment)} onOpenChange={(o) => !o && setPayment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update payment</DialogTitle>
          </DialogHeader>
          {payment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Payment status</Label>
                <Select
                  value={payment.payment_status}
                  onValueChange={(v) => setPayment({ ...payment, payment_status: v })}
                >
                  <SelectTrigger data-testid="payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["unpaid", "pending", "paid", "waived", "refunded"].map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payment.payment_amount}
                  onChange={(e) => setPayment({ ...payment, payment_amount: e.target.value })}
                  data-testid="payment-amount-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayment(null)}>
              Cancel
            </Button>
            <Button onClick={savePayment} data-testid="save-payment-button">
              Save payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete family?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes {confirmDelete?.parent_name}, their students, and all uploaded
            records. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={remove}
              data-testid="confirm-delete-family"
            >
              Delete family
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
